import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useVisibleTimeTracker from 'src/katalMetrics/useVisibleTimeTracker';
import { ACTION_METHODS, METRIC_TYPE, SITE_SERVICES } from 'src/katalMetrics/metricsModels';
import {
  Box,
  Button,
  SpaceBetween,
  Table,
  Header,
  Badge,
  Modal,
  FormField,
  Input,
  Select,
  StatusIndicator,
  Container
} from '@amzn/awsui-components-react';
import SchedulerSetupModal from './SchedulerSetupModal';
import { listAllCruzerSchedules, deleteCruzerJobSchedule } from './Utils/CruzerAPIs';
import { formatDatasetUrl, getStatusIndicator } from './Utils/GeneralUtils';
import { getDataFromMetaByAppId } from 'src/utilities/MetaAPIs';
import { fetchApps } from '../Templates/Utils/AppActivityUtils';



const DatasetRefreshSchedule = () => {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showAddSuccess, setShowAddSuccess] = useState(false);
  const [addedScheduleName, setAddedScheduleName] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [dataFromMeta, setDataFromMeta] = useState([]);
  const [datasetOptions, setDatasetOptions] = useState([]);
  const [appNameMap, setAppNameMap] = useState({});

  const currentAppId = useSelector((globalState) => globalState.auth.currentAppId);
  const userId = useSelector((globalState) => globalState.auth.userId);

  const ln = useLocation();
  useVisibleTimeTracker([{
      metricName: 'actionPerformedBy',
      metricValue: userId,
      metricType: METRIC_TYPE.STRING,
      isMonitor: true
    },
    {
      metricName: 'pagePath',
      metricValue: `${ln.pathname}`,
      metricType: METRIC_TYPE.STRING,
      isMonitor: true
    }
  ], "timeSpent", ACTION_METHODS.TIME_SPENT_ON_PAGE, SITE_SERVICES.CRUZER);

  // Load initial data and meta data
  useEffect(() => {
    loadData();
    if (currentAppId) {
      loadDataFromMeta();
    }
    fetchApps().then(apps => {
      const map = {};
      apps.forEach(app => { map[app.app_id] = app.app_name; });
      setAppNameMap(map);
    }).catch(() => {});
  }, [currentAppId]);

  // Update dataset options when meta data changes
  useEffect(() => {
    if (dataFromMeta && typeof dataFromMeta === 'object') {
      const options = [];
      const seenUrls = new Set();
      
      if (dataFromMeta.process_details?.qs) {
        dataFromMeta.process_details.qs.forEach(qsItem => {
          if (qsItem.dependencies) {
            qsItem.dependencies.forEach(dep => {
              if (dep.dataset_url && Array.isArray(dep.dataset_url)) {
                dep.dataset_url.filter(url => url).forEach(url => {
                  if (!seenUrls.has(url)) {
                    seenUrls.add(url);
                    const urlParts = url.split('/');
                    const datasetId = urlParts[urlParts.length - 2];
                    
                    options.push({
                      label: `${qsItem.analysis_name} (${datasetId})`,
                      value: url
                    });
                  }
                });
              }
            });
          }
        });
      }
      
      setDatasetOptions(options);
    }
  }, [dataFromMeta]);

  const loadDataFromMeta = async () => {
    try {
      const response = await getDataFromMetaByAppId(currentAppId);
      setDataFromMeta(response);
    } catch (error) {
      console.error('Failed to load meta data:', error);
      setDataFromMeta([]);
    }
  };


  const loadData = async () => {
    setLoading(true);
    
    // Load meta data first if not already loaded
    let metaData = dataFromMeta;
    if (!metaData || Object.keys(metaData).length === 0) {
      try {
        metaData = await getDataFromMetaByAppId(currentAppId);
        setDataFromMeta(metaData);
      } catch (error) {
        console.error('Failed to load meta data:', error);
        metaData = {};
      }
    }
    
    listAllCruzerSchedules(
      currentAppId,
      async (response) => {
        const schedules = response.data?.listAllCruzerSchedules || [];
        
        const formattedData = schedules.map(schedule => {
          let datasetDisplayName = schedule.reference_id;
          
          // Try to find matching analysis name from current app's meta data
          if (metaData?.process_details?.qs) {
            metaData.process_details.qs.forEach(qsItem => {
              if (qsItem.dependencies) {
                qsItem.dependencies.forEach(dep => {
                  if (dep.dataset_url && Array.isArray(dep.dataset_url)) {
                    dep.dataset_url.forEach(url => {
                      if (!url) return;
                      const urlParts = url.split('/');
                      const datasetId = urlParts[urlParts.length - 2];
                      if (datasetId === schedule.reference_id) {
                        datasetDisplayName = `${qsItem.analysis_name} (${datasetId})`;
                      }
                    });
                  }
                });
              }
            });
          }
          
          return {
            schedulerId: schedule.schedule_id,
            schedulerName: schedule.schedule_name,
            datasetUrl: datasetDisplayName,
            dailyRefreshCount: schedule.daily_schedule_count ?? 'N/A',
            nextRefresh: null,
            status: schedule.is_active ? 'Active' : 'Disabled',
            associatedApps: schedule.app_ids?.join('\n') || 'N/A',
            originalData: schedule
          };
        });

        // For schedules where display name wasn't resolved (cross-app), fetch meta from their other app IDs
        // This block of code will be removed once we have all dataset -> app information from META
        const unresolvedSchedules = formattedData.filter(s => s.datasetUrl === s.originalData.reference_id);
        if (unresolvedSchedules.length > 0) {
          const otherAppIds = [...new Set(
            unresolvedSchedules.flatMap(s => (s.originalData.app_ids || []).filter(id => id !== currentAppId))
          )];
          const otherMetaResults = await Promise.allSettled(otherAppIds.map(id => getDataFromMetaByAppId(id)));
          const otherMetaMap = {};
          otherAppIds.forEach((id, i) => {
            if (otherMetaResults[i].status === 'fulfilled') otherMetaMap[id] = otherMetaResults[i].value;
          });
          unresolvedSchedules.forEach(s => {
            for (const appId of (s.originalData.app_ids || [])) {
              const meta = otherMetaMap[appId];
              if (!meta?.process_details?.qs) continue;
              for (const qsItem of meta.process_details.qs) {
                for (const dep of (qsItem.dependencies || [])) {
                  for (const url of (dep.dataset_url || [])) {
                    if (!url) continue;
                    const datasetId = url.split('/').slice(-2, -1)[0];
                    if (datasetId === s.originalData.reference_id) {
                      s.datasetUrl = `${qsItem.analysis_name} (${datasetId})`;
                    }
                  }
                }
              }
            }
          });
        }

        setDatasets(formattedData);
        setLoading(false);
      },
      (error) => {
        console.error('Failed to load schedules:', error);
        setDatasets([]);
        setLoading(false);
      }
    );
  };

  const handleView = (dataset) => {
    setModalMode('view');
    setSelectedDataset(dataset);
    setShowViewModal(true);
  };

  const handleEdit = (dataset) => {
    setModalMode('edit');
    setSelectedDataset(dataset);
    setShowEditModal(true);
  };

  const handleDeleteConfirm = (dataset) => {
    setSelectedDataset(dataset);
    setShowDeleteConfirm(true);
  };

  const handleDelete = () => {
    if (!selectedDataset?.schedulerId) {
      console.error('No schedule ID found for deletion');
      return;
    }

    const deleteInput = {
      schedule_id: selectedDataset.schedulerId
    };

    setDeleteLoading(true);
    deleteCruzerJobSchedule(
      deleteInput,
      (response) => {
        loadData()
        setDeleteLoading(false);
        setShowDeleteConfirm(false);
        setShowDeleteSuccess(true);
      },
      (error) => {
        console.error('Failed to delete schedule:', error);
        setDeleteLoading(false);
        setShowDeleteConfirm(false);
      }
    );
  };

  const handleAddSchedule = () => {
    setSelectedDataset(null);
    setModalMode('add');
    setShowAddModal(true);
  };

  const columnDefinitions = [
    {
      id: 'schedulerName',
      header: 'Scheduler name',
      cell: item => item.schedulerName,
      sortingField: 'schedulerName',
      width: 200
    },
    {
      id: 'datasetName',
      header: 'Dataset Name',
      cell: item => item.datasetUrl,
      sortingField: 'datasetUrl',
      width: 220
    },
    {
      id: 'dailyRefreshCount',
      header: 'Daily Refresh Count',
      cell: item => item.dailyRefreshCount,
      sortingField: 'dailyRefreshCount',
      width: 180
    },
    // {
    //   id: 'nextRefresh',
    //   header: 'Next Refresh',
    //   cell: item => formatDateTime(item.nextRefresh),
    //   sortingField: 'nextRefresh',
    //   width: 180
    // },
    {
      id: 'status',
      header: 'Status',
      cell: item => getStatusIndicator(item.status),
      sortingField: 'status',
      width: 120
    },
    {
      id: 'associatedApps',
      header: 'Associated Apps',
      cell: item => (
        <Box>
          {item.associatedApps.split('\n').map((appId, index) => (
            <div key={index}>{appNameMap[appId] || appId}</div>
          ))}
        </Box>
      ),
      width: 200
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="normal"
            onClick={() => handleView(item)}
          >
            View
          </Button>
          <Button
            variant="icon"
            iconName="edit"
            onClick={() => handleEdit(item)}
          />
          <Button
            variant="icon"
            iconName="remove"
            onClick={() => handleDeleteConfirm(item)}
          />
        </SpaceBetween>
      ),
      width: 180
    }
  ];

  return (
    <Box padding={{ vertical: 'l', horizontal: 'l' }}>
        <Table
          columnDefinitions={columnDefinitions}
          items={datasets}
          loading={loading}
          loadingText="Loading schedules..."
          empty={
            <Box textAlign="center" color="inherit">
              <b>No scheduled refreshes</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No dataset refresh schedules found.
              </Box>
            </Box>
          }
          header={
            <Header
              actions={
                <Button variant="primary" onClick={handleAddSchedule}>
                  Add schedule
                </Button>
              }
            >
              Dataset Refresh Schedules
            </Header>
          }
        />

      {/* Scheduler Setup Modal - for View, Edit, and Add */}
      <SchedulerSetupModal
        key={`${modalMode}-${selectedDataset?.schedulerId || 'add'}`}
        visible={showViewModal || showEditModal || showAddModal}
        onDismiss={() => {
          setShowViewModal(false);
          setShowEditModal(false);
          setShowAddModal(false);
          setSelectedDataset(null);
        }}
        initialData={selectedDataset}
        mode={modalMode}
        loadData={loadData}
        allDatasets={datasets}
        setDatasets={setDatasets}
        setShowEditModal={setShowEditModal}
        setShowAddModal={setShowAddModal}
        setSelectedDataset={setSelectedDataset}
        setShowAddSuccess={setShowAddSuccess}
        setAddedScheduleName={setAddedScheduleName}
        dataFromMeta={dataFromMeta}
        datasetOptions={datasetOptions}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteConfirm}
        onDismiss={() => setShowDeleteConfirm(false)}
        header="Confirm Delete"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleDelete}
                loading={deleteLoading}
                disabled={deleteLoading}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>
          Are you sure you want to delete the schedule{' '}
          <strong>{selectedDataset?.schedulerName}</strong>?
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal
        visible={showDeleteSuccess}
        onDismiss={() => {
          setShowDeleteSuccess(false);
        }}
        header="Delete Successful"
        footer={
          <Box float="right">
            <Button 
              variant="primary" 
              onClick={() => {
                setShowDeleteSuccess(false);
              }}
            >
              OK
            </Button>
          </Box>
        }
      >
        <Box>
          Schedule <strong>{selectedDataset?.schedulerName}</strong> has been successfully deleted.
        </Box>
      </Modal>

      {/* Add Success Modal */}
      <Modal
        visible={showAddSuccess}
        onDismiss={() => {
          loadData();
          setShowAddSuccess(false);
          setAddedScheduleName('');
        }}
        header="Schedule Added Successfully"
        footer={
          <Box float="right">
            <Button 
              variant="primary" 
              onClick={() => {
                loadData();
                setShowAddSuccess(false);
                setAddedScheduleName('');
              }}
            >
              Okay
            </Button>
          </Box>
        }
      >
        <Box>
          Schedule <strong>{addedScheduleName}</strong> has been successfully added.
        </Box>
      </Modal>
    </Box>
  );
};

export default DatasetRefreshSchedule;
